import { Notice } from '@plato/ui-legacy/components/ui/notice';
import { AlertTriangle } from 'lucide-react';
import type React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';

/**
 * Stale-data warning banner for the home dashboard. Renders nothing while loading, on
 * error, when data is fresh, or when the tenant has no orders. When the tenant's ERP data
 * hasn't refreshed in at least one full working day (decided server-side), shows an amber
 * banner whose text reads "over one day" / "over {{count}} days".
 */
export function DataFreshnessIndicator(): React.JSX.Element | null {
    const { t } = useTranslation();
    const sendEvent = useAnalytics();
    const { data } = api.platoUser.query.dataFreshness.getDataFreshness.useQuery(undefined, {
        staleTime: 5 * 60 * 1000,
    });

    const isStale = data?.isStale ?? false;
    const staleWorkingDays = data?.staleWorkingDays ?? 0;

    useEffect(() => {
        if (isStale) {
            sendEvent('data_freshness_warning_shown', { days: staleWorkingDays });
        }
    }, [isStale, staleWorkingDays, sendEvent]);

    if (!isStale) {
        return null;
    }

    return (
        <Notice
            variant="warning"
            className="border-amber-300 bg-amber-50 text-amber-700"
            icon={AlertTriangle}
            title={t('data_freshness__stale')}
            description={t('data_freshness__stale_detail', { count: staleWorkingDays })}
        />
    );
}
